import { useApi } from "@/hooks/use-api";
import { cn } from "@/lib/utils";
import { useAppState } from "@/store/useApp";
import { FileType } from "@/types/document";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { ImportSettingsDialog } from "./ImportSettingsDialog";

interface ImportProgress {
  current: number;
  total: number;
  currentFileName: string;
  isImporting: boolean;
}

// Valid file extensions based on FileType
const VALID_EXTENSIONS: Record<FileType, string[]> = {
  pdf: [".pdf"],
  txt: [".txt"],
  md: [".md"],
};

// Get all valid extensions as a flat array
const getAllValidExtensions = (): string[] => {
  return Object.values(VALID_EXTENSIONS).flat();
};

// Check if a file has a valid extension
const isValidFileType = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return getAllValidExtensions().some((ext) => fileName.endsWith(ext));
};

// Check if drag event contains files (files array is empty during drag, only available on drop)
const hasFiles = (dataTransfer: DataTransfer): boolean => {
  return dataTransfer.types.includes("Files");
};

export const DocumentImport = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    current: 0,
    total: 0,
    currentFileName: "",
    isImporting: false,
  });
  const { importDocument } = useApi();
  const assessments = useAppState((state) => state.assessments || []);
  const courses = useAppState((state) => state.courses || []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Show drag state if files are being dragged
    if (hasFiles(e.dataTransfer)) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Allow drop if files are being dragged
    if (!hasFiles(e.dataTransfer)) {
      e.dataTransfer.dropEffect = "none";
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    // Filter to only include valid file types
    const validFiles = files.filter(isValidFileType);
    const invalidFiles = files.filter((file) => !isValidFileType(file));

    // Show toast warning for invalid files
    if (invalidFiles.length > 0) {
      const supportedTypes = getAllValidExtensions().join(", ");
      if (invalidFiles.length === files.length) {
        // All files are invalid
        toast.error(
          `Unsupported file type${
            files.length > 1 ? "s" : ""
          }. Only ${supportedTypes} files are supported.`
        );
      } else {
        // Some files are invalid
        toast.warning(
          `${invalidFiles.length} file${
            invalidFiles.length > 1 ? "s" : ""
          } ignored. Only ${supportedTypes} files are supported.`
        );
      }
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setImportError(null); // Clear any previous errors
      setShowImportDialog(true);
    }
  }, []);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const filesArray = Array.from(files);
        // Filter to only include valid file types
        const validFiles = filesArray.filter(isValidFileType);
        const invalidFiles = filesArray.filter(
          (file) => !isValidFileType(file)
        );

        // Show toast warning for invalid files
        if (invalidFiles.length > 0) {
          const supportedTypes = getAllValidExtensions().join(", ");
          if (invalidFiles.length === filesArray.length) {
            // All files are invalid
            toast.error(
              `Unsupported file type${
                filesArray.length > 1 ? "s" : ""
              }. Only ${supportedTypes} files are supported.`
            );
          } else {
            // Some files are invalid
            toast.warning(
              `${invalidFiles.length} file${
                invalidFiles.length > 1 ? "s" : ""
              } ignored. Only ${supportedTypes} files are supported.`
            );
          }
        }

        if (validFiles.length > 0) {
          setSelectedFiles(validFiles);
          setImportError(null); // Clear any previous errors
          setShowImportDialog(true);
        }
      }

      // Reset the file input value to allow selecting the same file(s) again
      e.target.value = "";
    },
    []
  );

  const handleClick = () => {
    document.getElementById("file-input")?.click();
  };

  const handleBatchImport = async (assessmentId: string | null) => {
    if (selectedFiles.length === 0) return;

    // Clear any previous errors when starting import
    setImportError(null);

    setProgress({
      current: 0,
      total: selectedFiles.length,
      currentFileName: "",
      isImporting: true,
    });

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        setProgress((prev) => ({
          ...prev,
          current: i + 1,
          currentFileName: file.name,
        }));

        await importDocument(file, assessmentId);
      }

      // Import completed successfully
      toast.success(
        `${selectedFiles.length} document${
          selectedFiles.length > 1 ? "s" : ""
        } queued for import. We'll send you an email when done.`
      );
      setShowImportDialog(false);
      setSelectedFiles([]);
    } catch (error) {
      console.error("Error during batch import:", error);

      // Check if it's a 400 error with detailed message
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        error.status === 400
      ) {
        const message = error instanceof Error ? error.message : "Bad request";
        setImportError(message);
      } else {
        setImportError("Import failed");
      }
    } finally {
      setProgress((prev) => ({ ...prev, isImporting: false }));
    }
  };

  return (
    <>
      <div
        className={cn(
          "fixed bottom-10  bg-[#FFFBF9] border-[#FFA794] w-[800px] h-[120px] cursor-pointer rounded-md border border-dashed items-center",
          isDragging
            ? "bg-[#FFF1ED] border-brand text-brand"
            : "border-dashed border",
          "hover:bg-[#FFF1ED] hover:border-brand hover:text-brand"
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div className="flex px-10 py-16 h-full items-center justify-center gap-4">
          <div>
            <span className="font-semibold">Click to upload&nbsp;</span>
            <span>or drag and drop</span>
          </div>
          <input
            id="file-input"
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept={getAllValidExtensions().join(",")}
            multiple
          />
        </div>
      </div>

      <ImportSettingsDialog
        isOpen={showImportDialog}
        onClose={() => {
          setShowImportDialog(false);
          setSelectedFiles([]);
          setImportError(null); // Clear error when closing
          setProgress({
            current: 0,
            total: 0,
            currentFileName: "",
            isImporting: false,
          });
        }}
        onImport={handleBatchImport}
        selectedFiles={selectedFiles}
        progress={progress}
        assessments={assessments}
        courses={courses}
        error={importError}
      />
    </>
  );
};
